import { useMutation, useQuery, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import type { TourSlotSuggestion } from 'src/services/tour-availability/types';

export const TOUR_SUGGESTIONS_PATH = 'api/landlord/tour-suggestions';

type SuggestionResponse = {
    tour_slot_suggestion: TourSlotSuggestion | null;
};

export const getOpenTourSuggestionQueryKey = (leadUuid: string) => [
    queryKeys.tourSlotSuggestion,
    leadUuid,
];

export const useOpenTourSuggestion = (leadUuid: string, enabled = true) =>
    useQuery(
        getOpenTourSuggestionQueryKey(leadUuid),
        async () => {
            const response = await apiClient(`${TOUR_SUGGESTIONS_PATH}/open`, {
                searchParams: { lead_uuid: leadUuid },
            }).json<SuggestionResponse>();
            return response.tour_slot_suggestion;
        },
        {
            enabled: enabled && !!leadUuid,
            retry: () => false,
        }
    );

export const useCreateTourSuggestions = (leadUuid: string) => {
    const queryClient = useQueryClient();

    return useMutation<TourSlotSuggestion | null, unknown, { messageUuid: string }>(
        async ({ messageUuid }) => {
            const response = await apiClient(`${TOUR_SUGGESTIONS_PATH}/suggestions`, {
                method: 'post',
                json: { message_uuid: messageUuid },
            }).json<SuggestionResponse>();
            return response.tour_slot_suggestion;
        },
        {
            onSuccess: () =>
                queryClient.invalidateQueries(getOpenTourSuggestionQueryKey(leadUuid)),
        }
    );
};

/** Dismiss a live suggestion so it no longer shows. */
export const useDismissTourSuggestion = (leadUuid: string) => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, { suggestionId: number }>(
        async ({ suggestionId }) => {
            await apiClient(`${TOUR_SUGGESTIONS_PATH}/${suggestionId}/dismiss`, {
                method: 'patch',
            });
        },
        {
            onSuccess: () =>
                queryClient.invalidateQueries(getOpenTourSuggestionQueryKey(leadUuid)),
        }
    );
};
